import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { ProgressBar } from '@fluentui/react-components';
import { useTopBarActions, useTopBarTitle } from '../../../contexts/TopBarContext';
import { useDashboard } from '../../../contexts/DashboardContext';
import { useCreationPolling } from '../../dashboard-creation/hooks/useCreationPolling';
import type { DashboardCreationStatus } from '../../../api/types';
import { useTheme } from '../../../contexts/ThemeContext';
import FilterSkeleton from './FilterSkeleton';
import DashboardLayoutSkeleton from './DashboardLayoutSkeleton';
import { MutationButton, useWorkspaceAccess } from '../../security/WorkspaceAccess';
import './skeleton.css';

// ─── Props ──────────────────────────────────────────────────────────────────

interface DashboardGenerationViewProps {
  /** Active job ID for polling generation status. */
  jobId: string;
  /** Dashboard ID — used to update the sidebar/list name when plan preview arrives. */
  dashboardId: string;
  /** Dashboard name shown in progress label. */
  dashboardName: string;
  /** Called when generation completes successfully. */
  onComplete: (dashboardId?: string) => void;
  /** Called to initiate cancel. */
  onCancel: () => void;
  /** Called when backend confirms cancellation. */
  onCancelled: () => void;
  /** Called when generation fails — parent handles the failed UI. */
  onFailed: () => void;
  /** Called to retry generation (optional — parent may handle retry in failed UI). */
  onRetry?: () => void;
  /** Whether a cancel request is in-flight. */
  isCancelling?: boolean;
}

/**
 * DashboardGenerationView — replaces the old text-based CreationProgress
 * with a dashboard-shaped skeleton that gives the user an immediate sense
 * of the final layout.
 *
 * Responsibilities:
 * 1. Show a progress bar at the top
 * 2. Render filter skeleton row
 * 3. Render chart-type skeletons in a grid layout
 * 4. When generation completes, trigger transition to real dashboard
 * 5. Support session restore on navigation back
 */
const DashboardGenerationView: React.FC<DashboardGenerationViewProps> = ({
  jobId,
  dashboardId,
  dashboardName,
  onComplete,
  onCancel,
  onCancelled,
  onFailed,
  isCancelling = false,
}) => {
  const { isDarkMode } = useTheme();
  const { runMutation } = useWorkspaceAccess();
  const { updateDashboardInList } = useDashboard();
  const [hasCompleted, setHasCompleted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up transition timer on unmount to avoid stale state updates
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  // Poll generation status
  const { status } = useCreationPolling({
    jobId,
    onCancelled,
    onComplete: useCallback(
      (completedStatus: DashboardCreationStatus) => {
        // Show "Dashboard ready!" for 1.5s before transitioning
        setIsTransitioning(true);
        transitionTimerRef.current = setTimeout(() => {
          transitionTimerRef.current = null;
          setHasCompleted(true);
          onComplete(completedStatus.dashboardId);
        }, 1500);
      },
      [onComplete],
    ),
  });

  // Update top bar title when plan preview name arrives from Phase 1
  const planName = status?.planPreview?.name;
  const planDescription = status?.planPreview?.description;
  useTopBarTitle(planName || dashboardName || 'Dashboard');

  // Update sidebar/list name when plan preview name arrives
  const appliedNameRef = useRef<string | null>(null);
  useEffect(() => {
    if (planName && planName !== appliedNameRef.current) {
      appliedNameRef.current = planName;
      updateDashboardInList(dashboardId, {
        name: planName,
        ...(planDescription ? { description: planDescription } : {}),
      });
    }
  }, [planName, planDescription, dashboardId, updateDashboardInList]);

  const isTerminal =
    status?.state === 'completed' ||
    status?.state === 'failed' ||
    status?.state === 'cancelled';

  const progressPct = isTransitioning ? 100 : (status ? Math.round(status.progress * 100) : 0);
  const progressMessage = isTransitioning
    ? 'Dashboard ready!'
    : isCancelling
      ? 'Canceling…'
      : (status?.message || `Generating ${dashboardName || 'dashboard'}…`);

  // ── TopBar: Cancel button ─────────────────────────────────────────────
  const topBarActions = useMemo(
    () => (
      <MutationButton
        appearance="secondary"
        onClick={() => runMutation(onCancel)}
        disabled={isCancelling || isTerminal}
      >
        Cancel
      </MutationButton>
    ),
    [onCancel, runMutation, isCancelling, isTerminal],
  );

  useTopBarActions(topBarActions);

  // ── Failed state — delegate to parent via onFailed callback ────────────
  const failedHandledRef = useRef(false);
  useEffect(() => {
    if (status?.state === 'failed' && !failedHandledRef.current) {
      failedHandledRef.current = true;
      onFailed();
    }
  }, [status?.state, onFailed]);

  if (status?.state === 'failed') {
    return null; // Parent will render StoppedDashboardView
  }

  // Cancelled state is handled by the parent (DashboardViewPage)
  // via the onCancelled callback — no terminal rendering here.

  // Don't render skeleton if already completed (parent will swap to Dashboard)
  if (hasCompleted) {
    return null;
  }

  // During transition pause, keep showing the skeleton at 100%

  // ── Active generation — skeleton view ──────────────────────────────────
  return (
    <div
      className="dashboard-generation-view"
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      {/* Progress bar */}
      <div className="generation-progress-bar">
        <span className="generation-progress-bar__label">
          {progressMessage}
        </span>
        <div className="generation-progress-bar__bar">
          <ProgressBar value={isTransitioning ? 1 : (status?.progress ?? 0)} max={1} />
        </div>
        <span className="generation-progress-bar__pct">{progressPct}%</span>
      </div>

      {/* Filter skeleton — shows real filter names after plan preview arrives.
          When readyFilters arrive, each validated filter gets a confirmed state. */}
      <FilterSkeleton
        filters={status?.planPreview?.filters}
        readyFilters={status?.readyFilters}
      />

      {/* Chart skeletons — default layout until readyCharts arrive in Phase 2.
          Plan preview charts are NOT used for skeleton layout to avoid
          jarring layout changes (count/type shifts) after Phase 1. */}
      <div className="dashboard-generation-view__content">
        <DashboardLayoutSkeleton
          readyCharts={status?.readyCharts}
        />
      </div>
    </div>
  );
};

export default DashboardGenerationView;